// BRIEF 08 (B.2 tokens) — fixed, value-exact design tokens.
//
// Structure mirrors themeDerivation.ts (grouped by role), but these are FIXED
// value-exact constants: each token resolves to the EXACT literal it replaces,
// so every rendered colour / radius / size is byte-identical under every theme
// (dark / light / blue). They are deliberately NOT wired to the live theme vars
// (--ac, --gn, ...): tokenizing a hardcoded colour to var(--ac) would make it
// theme-track, which is a SEPARATE later improvement (see
// theme_identity_matches.md), not this cure.
//
// JS/TS sites import these constants directly (colours stay real hex strings, so
// values that get hex-parsed / stored / compared keep working). CSS-file sites
// use the parallel fixed custom properties added to the :root block in App.css
// (plain CSS has no other token mechanism); those --c-* / --rad-* / --fs-*
// properties are fixed and never overridden by a theme.

// A type-only import: erased by the compiler, so this stays the leaf module
// with no runtime edge that every view can import.
import type { CSSProperties } from "react";

// ============================ COLOURS (113) ============================
// One token per distinct census literal, case-exact (no folding: #4a7cff and
// #4A7CFF are separate tokens holding their exact-case string).

export const CT = {
  // — Colours that byte-match a themeDerivation base (fixed here; style-position
  //   occurrences are the theme-tracking candidates listed in theme_identity_matches.md) —
  c_141820: "#141820", // == --bd / --bs
  c_161b26: "#161b26", // == --bi
  c_1A1F2B: "#1A1F2B", // == --bc
  c_1a1f2b: "#1a1f2b", // == --bc
  c_1f2537: "#1f2537", // == --bh
  c_252d3a: "#252d3a", // == --br
  c_34D399: "#34D399", // == --gn
  c_34d399: "#34d399", // == --gn
  c_3a4358: "#3a4358", // == --cb
  c_4A7CFF: "#4A7CFF", // == --ac
  c_4a7cff: "#4a7cff", // == --ac
  c_5a6478: "#5a6478", // == --tm
  c_5a9d80: "#5a9d80", // == --gnm
  c_8892A4: "#8892A4", // == --ts
  c_8892a4: "#8892a4", // == --ts
  c_A78BFA: "#A78BFA", // == --pp
  c_E8ECF4: "#E8ECF4", // == --tp
  c_F87171: "#F87171", // == --rd
  c_FBBF24: "#FBBF24", // == --am
  c_a78bfa: "#a78bfa", // == --pp
  c_cf7a7a: "#cf7a7a", // == --rdm
  c_e0e4ea: "#e0e4ea", // == --ct
  c_e8ecf4: "#e8ecf4", // == --tp
  c_f87171: "#f87171", // == --rd
  c_fb923c: "#fb923c", // == --or
  c_fbbf24: "#fbbf24", // == --am

  // — Neutral / independent UI colours (greys, borders, status, brand, login) —
  c_000: "#000",
  c_006666: "#006666",
  c_0078D7: "#0078D7",
  c_00B7C3: "#00B7C3",
  c_00a4ef: "#00a4ef",
  c_0A2E5C: "#0A2E5C",
  c_0B6A0B: "#0B6A0B",
  c_0c0f14: "#0c0f14",
  c_0f1115: "#0f1115",
  c_10b981: "#10b981",
  c_161822: "#161822",
  c_16C60C: "#16C60C",
  c_1B1B1B: "#1B1B1B",
  c_1B2A4A: "#1B2A4A",
  c_1a1a1a: "#1a1a1a",
  c_1a1d24: "#1a1d24",
  c_1e2130: "#1e2130",
  c_1f2937: "#1f2937",
  c_222: "#222",
  c_22d3ee: "#22d3ee",
  c_233456: "#233456",
  c_252838: "#252838",
  c_2563EB: "#2563EB",
  c_2a2e37: "#2a2e37",
  c_2a5db0: "#2a5db0",
  c_2b3e62: "#2b3e62",
  c_2e3248: "#2e3248",
  c_2f2f2f: "#2f2f2f",
  c_334d73: "#334d73",
  c_34515E: "#34515E",
  c_3a3a3a: "#3a3a3a",
  c_401B6C: "#401B6C",
  c_4C2C18: "#4C2C18",
  c_4D4D4D: "#4D4D4D",
  c_4a5060: "#4a5060",
  c_4e8a70: "#4e8a70",
  c_556B2F: "#556B2F",
  c_5D7079: "#5D7079",
  c_64748b: "#64748b",
  c_666: "#666",
  c_6E0F3C: "#6E0F3C",
  c_6b7280: "#6b7280",
  c_750B1C: "#750B1C",
  c_7fba00: "#7fba00",
  c_8764B8: "#8764B8",
  c_888: "#888",
  c_888888: "#888888",
  c_8CBD18: "#8CBD18",
  c_8E562E: "#8E562E",
  c_939393: "#939393",
  c_94a3b8: "#94a3b8",
  c_9ca3af: "#9ca3af",
  c_A74109: "#A74109",
  c_C19C00: "#C19C00",
  c_C30052: "#C30052",
  c_E81123: "#E81123",
  c_F7630C: "#F7630C",
  c_FCE100: "#FCE100",
  c_a16207: "#a16207",
  c_a3e635: "#a3e635",
  c_b86464: "#b86464",
  c_b87333: "#b87333",
  c_bbb: "#bbb",
  c_c084fc: "#c084fc",
  c_ccc: "#ccc",
  c_d1d5db: "#d1d5db",
  c_d33: "#d33",
  c_dc2626: "#dc2626",
  c_dfe1e5: "#dfe1e5",
  c_e2e8f0: "#e2e8f0",
  c_e8eaed: "#e8eaed",
  c_e8eaf0: "#e8eaf0",
  c_e8eef6: "#e8eef6",
  c_ea580c: "#ea580c",
  c_eee: "#eee",
  c_ef4444: "#ef4444",
  c_f0f2f5: "#f0f2f5",
  c_f0f4fa: "#f0f4fa",
  c_f25022: "#f25022",
  c_f472b6: "#f472b6",
  c_f59e0b: "#f59e0b",
  c_f5f6f8: "#f5f6f8",
  c_f8f9fb: "#f8f9fb",
  c_feed: "#feed",
  c_ffb900: "#ffb900",
  c_fff: "#fff",
  c_ffffff: "#ffffff",
} as const;

// ============================ RADII (16) ============================
// Value-exact; one token per distinct rendered radius. JS number N and CSS "Npx"
// share a token (identical rendered radius). --rd radius collision is NOT touched.

export const RAD = {
  r0: 0,
  r2: 2,
  r3: 3,
  r4: 4,
  r5: 5,
  r6: 6,
  r7: 7,
  r8: 8,
  r10: 10,
  r12: 12,
  r14: 14,
  r20: 20,
  r999: 999,
  pill: "50%",
  tabTop: "3px 3px 0 0",
  tabBottom: "0 0 3px 3px",
} as const;

// ============================ SIZES (24) ============================
// Value-exact; one token per distinct rendered font-size. JS number N and CSS
// "Npx" share a token. Responsive var(--base-font-size)/calc() sizes and ternary
// expressions are left as-is (not fixed literals).

/* 195 §A.4 — THE ONE DIMMING. An archived task or event in the Archive is
   the same card the matter shows, dimmed (Laurent, 2026-09-18: "make them
   dimmed though so nobody confuses them with active tasks/events"). MEASURED
   before a value was chosen: the tree has NO muted or disabled SURFACE token —
   themeDerivation's vocabulary carries muted TEXT (`--tm`), two muted status
   inks (`--gnm`, `--rdm`) and nothing for a whole row — and it dims rows by
   OPACITY in three places at ONE value, 0.55: a completed or archived task
   row and an invoiced time-entry row (CaseTaskRow, TimeEntryTable). That value
   is the definition, named once here and read by those sites and by the
   Archive's cards and lines; `--bh`'s order (1.08 : 1 in dark, 194E) is what a
   surface token would have given and is not reached for. The contrast it
   leaves — primary text at this opacity over the card, both shipped themes —
   is measured in the browser and recorded at 195's close.
   195B §C [RULED — Laurent, 2026-09-19: "decrease the dimming a little bit
   across the app, all the same (make it A BIT more visible), no divergence"]
   — 0.55 → 0.70. At 0.55 the dimmed primary text read 2.56 : 1 on the light
   default theme (WATT, the one he reads) and 5.04 : 1 in dark; at 0.70,
   COMPUTED with the tree's own formula over the stored tokens and then read
   off the running staging: 3.54 : 1 (a card over the page) / 3.60 (a row in
   a card) on WATT, 7.43 / 7.45 in dark — above the 3 : 1 line the audit
   draws, and still plainly under the live row's 7.08 / 13.91. 0.65 clears the
   line by a hair (3.17); 0.75 reads as a live row with a cold. AND THE
   "THREE PLACES" ABOVE WERE TWO: the invoiced time-entry row
   (TimeEntryTable) still carried its own `0.55` literal — measured at this
   brief, wired to this name so the ruling's "no divergence" holds. */
export const DIMMED_ROW_OPACITY = 0.7;

/* 204 Part Three §5 [RULED — Laurent: "no padding in the popup after login and
   in the bell popup, text against window borders — fix it using the same
   padding as the communications popup"] — THE HEADER POPUPS' INSET. It is
   the calls panel's own ("Internal communication" — the communications
   popup, CallsPanel.tsx, both arms) since Phase 31, named here so the bell
   (App.tsx, its box carried `padding: 4`: MEASURED 5 px from the border to
   "OVERDUE", the quote and the sign-in line, against the calls panel's 11)
   reads the same value and not a copy of it. The after-login overview is
   the modal shell (`.modal-body` 12px 16px, measured 16 – 17 px everywhere)
   and takes nothing from here.

   205 §2.1 [RULED — Laurent: "augment the padding with 25 % in all popout
   windows (login popup, bell, communication, timers, chats)"] — RAISED BY A
   QUARTER: 8 → 10, 10 → 12.5. THE 25 % IS THE RULING AND NOT A DERIVATION —
   nothing in the tree implies it, no measurement produced it, and it is
   written here so the next reader does not go looking for arithmetic that
   was never done. §2.2 brings the other three pop-outs onto it.

   205 §2.2 — AND THE LITERAL IS GONE: the quarter is `--popup-pad-scale`
   (App.css `:root`, where CSS can read it too — two of the five pop-outs are
   stylesheet rules and one is a separate document), and this token is that
   var over ITS OWN base. It still computes to 10px 12.5px, so the two call
   sites reading it (the bell, the calls panel) are unchanged by this commit;
   what changed is that the other three now scale from the same one figure
   instead of from three edits. */
export const popupPad = (...px: readonly number[]): string =>
  px.map((n) => `calc(${String(n)}px * var(--popup-pad-scale))`).join(" ");

export const POPUP_PAD = popupPad(8, 10);

/* 206B §4 [RULED — Laurent: «OK except chat should be twice as much»] — THE
   CHAT POP-OUT'S INSET IS TWICE WHAT IT WAS, AND THE DOUBLING IS ITS OWN NAMED
   FIGURE ON TOP OF THE SHARED SCALE, not a new base and not a second scale.
   The other four pop-outs (the after-login overview, the bell, the
   communication panel, the timers) stay exactly at `--popup-pad-scale`; only
   the chat's four arms (ChatView.tsx — the general chat's feed and composer,
   the case chat's feed and composer, each gated on `popped`) read this. It is
   the SAME expression as `popupPad` with one more factor in it, so the emitted
   value reads `calc(6px * var(--popup-pad-scale) * 2)`: the base stays the
   chat's own 6/8 and 4/8/2 (205 §2.2's census), a later ruling on the shared
   scale still reaches the chat through the var, and a later ruling on the
   chat alone is this one number. The factor is Laurent's, not a derivation. */
export const CHAT_POPUP_PAD_FACTOR = 2;
export const chatPopupPad = (...px: readonly number[]): string =>
  px
    .map((n) => `calc(${String(n)}px * var(--popup-pad-scale) * ${String(CHAT_POPUP_PAD_FACTOR)})`)
    .join(" ");

/* 206D §4 [RULED — Laurent: "triple the padding under the message / link file
   fields on the bottom (there only)"] — THE ROOM UNDER THE CHAT POP-OUT'S
   COMPOSER, THREE TIMES WHAT IT WAS, AS ITS OWN NAMED FACTOR over the one base
   it multiplies. MEASURED FIRST (`scripts/drive-206d-glance.js --part=popchat`,
   Chromium and WebKit): between the last field — « Link case… » in the general
   chat, « Message… » in a case chat — and the window's bottom edge stood 7.69
   visual px, and they were the composer's bottom padding ALONE (the chat box is
   flush with the window): `chatPopupPad(4, 8, 2)`'s third figure, 6.25 CSS px.
   So only that base is multiplied — the composer's top and side insets, the
   feed's, and the in-app composers (which are not windows) do not move. The
   factor is Laurent's, not a derivation. */
export const CHAT_POPUP_FOOT_FACTOR = 3;

/* 205 §3 [RULED — Laurent: "in Eisenhowers, and in Bentos, DOUBLE the padding
   of the task/event cards"] — THE CARD'S OWN PADDING, EXTRACTED AT ITS PRESENT
   VALUE. The two places are deliberately identical and say so in their own
   comments: `EisenTaskCard.tsx` is the Eisenhower/Bento TASK card and
   `CaseBoardView.tsx` is the Bento MATTER card, whose block is described there
   as "EisenTaskCard's pane, line for line". A figure written twice on purpose
   is a figure that wants a name. THIS COMMIT IS THE MOVE ONLY — "6px 8px" is
   what both rendered before it and what both render after it; the doubling is
   the next commit, alone, so a glance that finds the cards wrong can name
   which of the two changed them.

   205 §3b — DOUBLED, as ruled: 6 → 12, 8 → 16. The figure is Laurent's, not a
   derivation. The three BETWEEN-CARD gaps double with it (BentoBoard.tsx's
   column 6 → 12, EisenhowerDialog's quadrant scroller 6 → 12, TaskMatrix's
   4 → 8), which is the correction he added — "the padding BETWEEN the cards
   too, so the present proportions are kept". NOTHING INSIDE A CARD MOVES: the
   census §3 required is in the commit message, and six of the nine gaps in
   this neighbourhood are intra-card or intra-heading (a badge beside its
   text), which doubled would be a card that looks broken. */
export const CARD_PAD = "12px 16px";

/* 206 §3 [RULED — Laurent: "in MY eisenhowers add a character's width: now it
   is flush with the quadrant titles, it should be one character in retreat"]
   and 206B §3 [RULED — Laurent: "OK, but do the same in case eisenhower"] —
   THE ONE-CHARACTER RETREAT OF A QUADRANT'S CARDS FROM ITS HEADING, now on
   both boards, so it is named once: `EisenhowerDialog`'s card list and
   `TaskMatrix`'s card list each carry it as a `marginLeft`, and 206B §2's card
   cap subtracts the same figure so the two cards come out the same width.
   A MARGIN, NEVER A PADDING: `box-sizing: border-box` is global (App.css), so
   a padding would sit INSIDE any width cap and eat a character of the card.
   `ch` so it is one character of the text it indents, at every text size. */
export const EISEN_RETREAT = "1ch";

/* 206 §5 [RULED — Laurent: "the cards become very oversized when the screen is
   wide — no such problem in the case Eisenhower. Limit their length to the
   current length of the original cards in the app, depending on the zoom factor
   chosen by the user in settings"] — THE BENTO COLUMN'S FLOOR, WHICH IS WHAT
   SETS A CARD'S WIDTH EVERYWHERE THE SAME CARD RENDERS.
   ⟨SUPERSEDED FOR THE DRAWER'S CAP BY 206B §2, below: the sibling he meant is
   the case matrix. The two figures stay named because BentoBoard reads them;
   what follows is 206 §5's census as it stood.⟩
   THE CENSUS §5 ASKED FOR: the sibling is the BENTO, not the case matrix.
   `BentoDialog.tsx:126` mounts the SAME component the drawer mounts
   (`EisenTaskCard`); `TaskMatrix.tsx` has its own inline card markup and is a
   different element, narrow only because its host is narrow. And what sets the
   width there is THE COLUMN, not the card: `.board-col` carries
   `flex: "1 1 240px"` with a definite `minWidth: 240` and `padding: 8`
   (BentoBoard.tsx), so a card is 240 − 16 = 224 at the column's floor. That
   figure was itself measured and ruled (190 glance (i)(b)): the automatic
   minimum made each column as wide as its longest card, and 240 is the floor
   at which four columns fit the case panel at 1600/xlarge.
   IT FOLLOWS THE TEXT SIZE BECAUSE THE WHOLE APP DOES: the zoom setting is a
   root `zoom` (App.tsx:1735-1743) and `--base-font-size` stays pinned at 13px,
   so every layout pixel in the app scales together — 224 layout px is the
   Bento card's width at small, medium, large and xlarge alike. A `ch` figure
   would have tracked the card's own font instead of the column that defines
   it, which is the wrong thing to follow here (§3's indent is the opposite
   case, and is `ch` for exactly that reason).
   THE MOVE AND THE USE ARE IN ONE COMMIT AND THE MOVE CHANGES NOTHING:
   BentoBoard rendered `1 1 240px` / `minWidth: 240` / `padding: 8` before this
   and renders the identical three after it (rule 3's spirit kept — the figure
   is not touched, only named, and the naming is what stops the drawer's cap
   drifting from the board it copies). */
export const BENTO_COL_MIN = 240;
export const BENTO_COL_PAD = 8;

/* 206B §2 [RULED — Laurent: «did not mention Bento, did mention sibling in
   TASK section»] — the drawer's card cap became the width a card has in ONE
   QUADRANT of a case's Tasks-section matrix (235 at 1280 … 555 at 2560),
   derived from the case panel by the case view's arithmetic here
   (`eisenCardMax` over `CASE_COL_GAP`, `MATRIX_GAP` and `MATRIX_QUAD_PAD`).
   206C §1 [RULED — Laurent: «use the case Eisenhower card, but widen it to a
   full task section width (= half app width)»] — AND THE CAP IS NOW THE WHOLE
   TASKS SECTION, measured by the drawer off App.css itself (the section probe
   and `useTasksSectionWidth`, EisenhowerDialog.tsx, where the census figures
   are). So `eisenCardMax` is DELETED, and `CASE_COL_GAP` WITH IT (206C §7.4):
   it was a copy of App.css's `.csGrid` column gap that TypeScript could not
   read, its one reader was `eisenCardMax`, and the probe reads the real gap —
   the copy is not used, so it is not kept. The two matrix figures below STAY:
   TaskMatrix reads them (its board gap and quadrant padding, full size), so
   they are still named once; no cap derives from them any more. */
/** TaskMatrix's full-size board gap and quadrant padding (compact keeps its 4s). */
export const MATRIX_GAP = 6;
export const MATRIX_QUAD_PAD = 8;

export const FS = {
  s0: 0,
  s6: 6,
  s7: 7,
  s7_5: 7.5,
  s8: 8,
  s8_5: 8.5,
  s9: 9,
  s9_5: 9.5,
  s10: 10,
  s10_5: 10.5,
  s11: 11,
  s11_5: 11.5,
  s12: 12,
  s12_5: 12.5,
  s13: 13,
  s14: 14,
  s15: 15,
  s16: 16,
  s17: 17,
  s18: 18,
  s20: 20,
  s22: 22,
  s24: 24,
  // BRIEF 91 §1e — the modal scroll hint, three times its old 9px. Not a census
  // literal (the B.2 tokens all were): the value did not exist in the tree
  // before this brief asked for it, and a raw 27 at the call site would be the
  // one hardcoded size in a file whose whole point is that there are none.
  s27: 27,
  em0_9: "0.9em",
} as const;

/* ── 204 §B.1 [CURE] — THE Z SCALE: A LOOKUP THAT PRESERVES EVERY EXISTING VALUE ─
 *
 * CENSUSED BY GREP on `28105f17` before anything was named (the brief's own
 * figures re-measured): `src/App.css` carries 19 `z-index` declarations on 27
 * matching lines (the other 8 are comments), 11 distinct values, 1 → 100100;
 * the inline `zIndex` sites under `src/` carry 24 distinct numeric literals
 * (0 → 100000) plus two prop-literals (301, 400), four named constants already
 * in the tree (`DRAWER_Z` 9991, `PANEL_Z` 90, `CALL_LAYER_Z` 100000,
 * `CALL_WINDOW_Z` 100001) and the computed expressions (`DRAWER_Z + 4`,
 * `zIndex - 1`, `Math.max(1, 9 - i)`, `sortedAssigns.length - i`, `zIndex + 1`,
 * `CALL_LAYER_Z + 2`), which stay computed — this table names their bases.
 *
 * THIS IS A CURE, NOT A SCALE ANYBODY DESIGNED. Every figure below is one the
 * tree already renders; §B.2 substitutes them figure for figure and the
 * rendered value moves nowhere (the DOM roster and the compiler are the proof,
 * no glance). A tier is NAMED FROM WHAT IS THERE: by the role every site at
 * that figure shares where they share one, and by its rung on the in-surface
 * ladder where they do not (App.css's own comment on `.teTable thead th` names
 * that ladder: sticky cells 2, the counters strip 3, the header 4). Where a
 * figure's uses part ways the name says the LAYER, never one use — `lift2` (204 §7.2 renamed the six layer-named rungs for what sits on them: `stickyHead` · `stickyCell` · `counters` · `tableHead` · `inlineMenu` · `panelPopover`; the sentence below stands as the naming's history) —
 * covers a sticky cell, a sticky modal footer, a badge's glyph over its dot
 * and the Bento anchor span alike. The two ruled ceilings (151G §1: the toast
 * at 100100, the call window at 100001 its runner-up) are here by name and by
 * figure, exactly. The improvement that would REASON about these tiers (a
 * drag-time lock-out, §B.3) is not this table's business.
 *
 * TWO PAIRS RECUR: a portalled menu paints its dismiss backdrop one rung under
 * its box — (50, 51) in the settings pane, (200, 201) the popover family,
 * (999, 1000, 1001) TimeField's and OutlookImportModal's portals, (9998, 9999)
 * the full-screen panes, (99998, 99999) the menus above every modal — and the
 * pair is named as a pair. CSS-file sites read the same figures through the
 * `--z-*` custom properties on `:root` (App.css, the B.2 tokens' idiom — plain
 * CSS cannot import this file); those carry the eleven tiers CSS uses and MUST
 * agree with this table by hand — no instrument checks it yet (surfaced).
 */
export const Z = {
  /** Dashboard's decorative layer under its content (`Dashboard.tsx`, the one 0) */
  under: 0,
  /** [204 §7.2 — was `lift1`, named for what sits on it] a sticky head or cell over the rows it scrolls past, and a pane over its ground: the theads of EntityList, CaseLibraryCopySection and OutlookImportModal, TeamAccessPanel's, DocTrackingMatrix's, the drawers' sticky rows, the schedule's overlays, Dashboard's content over its `under` layer, `.bentoPane` */
  stickyHead: 1,
  /** [204 §7.2 — was `lift2`] a table's sticky cells over its scrolling ones (`.costs-table td.sticky-col-*`, TeamAccessPanel's first column — the ladder App.css names: cells 2, the counters strip 3, the header 4), a sticky modal footer (CaseModal, CollectiveCaseView), a badge's glyph over its dot (SharedUI), the Bento anchor span (BentoBoard) */
  stickyCell: 2,
  /** [204 §7.2 — was `lift3`] the counters strip pinned to the page's floor, above the sticky cells and under the table header (CountersStrip) */
  counters: 3,
  /** [204 §7.2 — was `lift4`] a pinned table header over its cells and the counters strip (`.teTable thead th`, `.invTable thead th`, `.costs-table th.sticky-col-*` — CSS only) */
  tableHead: 4,
  /** [204 §7.2 — was `float`] a dropdown or head floating INSIDE its own surface, never portalled: the role menu (CaseTeamSection), the quadrant picker (TaskModal), the mention list (ChatView), the library's menu (LibraryView), CaseTaskRow's badge over its hover area, the Agenda's sticky day head */
  inlineMenu: 10,
  /** [204 §7.2 — was `float2`] a panel's own popover over its inline menus and sticky heads (TeamAccessPanel's), and the schedule grid's sticky corner cell, which must beat both its sticky row and column heads (ScheduleView) */
  panelPopover: 20,
  /** hover tips (`.ttRowTip`, `.teLockTip`) and the settings pane's colour menu backdrop */
  tip: 50,
  /** the settings pane's colour menu box, over its backdrop (SettingsView's OutlookColorSelect) */
  tipMenu: 51,
  /** BadgeHoverArea's panel (`PANEL_Z`) */
  badgePanel: 90,
  /** `.modal-overlay` — the modal shell's own overlay */
  modalOverlay: 100,
  /** SideFlyPanel's overlay: over the modal overlay, under the popover backdrop */
  sideFly: 190,
  /** the popover family's dismiss backdrop (`.fs-combo-backdrop`, TypeaheadPicker's BACKDROP, MultiSelect, SearchPopover, the confirm dialogs, InvoiceView, App's) */
  popoverBackdrop: 200,
  /** the popover box over it (`.fs-combo-menu`, MenuSelect's default, TypeaheadPicker) */
  popover: 201,
  /** a modal over a modal (`<ModalLayout zIndex={300}>`: LinksManagerDialog, SubtaskChecklist, RolePicker, DocumentEntryModal's default, the confirm dialog's default) */
  modal2: 300,
  /** a picker over that second modal (SubtaskChecklist) */
  modal2Menu: 301,
  /** the folder picker's own React root (OneDrivePicker's default) and the conflict dialog over it (MatterFileConflictDialog) */
  folderPicker: 400,
  /** a fixed backdrop: `.mobile-overlay`, TimeField's */
  portalBackdrop: 999,
  /** the fixed box over it: `.sidebar.mob`, TimeField's and Combobox's menus, OutlookImportModal's backdrop, DocTrackingMatrix's overlays */
  portal: 1000,
  /** OutlookImportModal's pane over its 1000 backdrop */
  portalMenu: 1001,
  /** the update banner (`.UpdateBanner`) and the auth step modal's overlay (AuthStepModal) */
  banner: 4000,
  /** the drawers (`DRAWER_Z`, SharedUI) — their dialogs sit at `+ 2` and `+ 4`, computed */
  drawer: 9991,
  /** a full-screen pane's backdrop (CaseTaskRow's portal, EmailDropLayer) */
  paneBackdrop: 9998,
  /** the full-screen panes and the menus over them (BentoDialog, EisenhowerDialog, PlacesView, CaseTaskRow, App's) */
  pane: 9999,
  /** the Bento picker (BentoPicker's mounts) and PlacesView's tips and edit button */
  picker: 10000,
  /** [RULED 204 §1 — Laurent: "a modal opened while a full-screen pane is open renders ABOVE that
   *  pane, and the pane stays open behind it"] the modal shell's overlay while a pane is open —
   *  `pane + 2`, the drawers' own `+ 2` (`DRAWER_Z + 2`), typed as a literal because the mirror
   *  cannot follow an expression. ModalLayout takes it from `PaneOpenContext` (lib/paneStacking.ts);
   *  no mount names it. The pickers such a modal hosts sit one over it, computed. */
  paneModal: 10001,
  /** [RULED 204 §1] a modal stacked over that one while a pane is open (a confirm, a picker dialog,
   *  the conflict dialog — every mount that passes a figure above `modalOverlay`) — `pane + 4`,
   *  the drawers' `+ 4`. */
  paneModal2: 10003,
  /** App's fixed notification stack */
  stack: 99996,
  /** the backdrop of a menu above every modal (FolderPropagationModal, CallsPanel, App's) */
  topBackdrop: 99998,
  /** that menu's box, and the fixed banners at its height (ColorOrVarPicker's backdrop, DemoBanner, the schedule's paint cursor, App's) */
  topMenu: 99999,
  /** the top of the ordinary scale: the call layer (`CALL_LAYER_Z`), App's fixed banners, ColorOrVarPicker's menu, ChatView's */
  top: 100000,
  /** [RULED 151G §1] the call window — the toast's runner-up, `top + 1` */
  callWindow: 100001,
  /** [RULED 151G §1] the toast — the ceiling; nothing stacks above it */
  toast: 100100,
} as const;

/* ── 166 §C — A MODAL SHELL'S OWN WIDTH CAP ───────────────────────────────────
 *
 * `.modal` carries `max-width: min(92vw, var(--modal-max, 100vw)) !important`
 * (`App.css:1057`), so an inline `max-width` on a `className="modal"` div is
 * INERT — a bang outranks it. 114 §3 discovered this and routed ModalLayout's
 * `maxWidth` prop through the custom property instead; the twenty shells that
 * open-code their own `.modal` div were never converted and went on naming
 * figures that did nothing.
 *
 * Laurent's ruling, 2026-09-04: NO MODAL IS BOUND TO THE WINDOW'S WIDTH — every
 * shell names a pixel cap and that cap works; the `min(92vw, …)` stays as the
 * small-screen guard only.
 *
 * WHY A HELPER and not sixteen inline objects: TypeScript rejects a custom
 * property in a `style` literal outright (`TS2353: '"--modal-max"' does not
 * exist in type 'Properties<…>'`, measured), so each site would otherwise carry
 * its own cast — sixteen casts the `any` ratchet would count, for one rule.
 * ModalLayout uses this too, so the number→px rule is written once.
 *
 * A number gets `px` spelled out (React appends a unit to known style
 * properties, never to a custom one); a string is passed through as written.
 */
export const modalCap = (v: number | string): CSSProperties =>
  ({
    "--modal-max": typeof v === "number" ? v.toString() + "px" : v,
  }) as CSSProperties;
